import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import { xor } from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useAvailablePerspectives } from './hooks';
import { WidgetsPerspective } from './models';

export interface WithPerspectives {
  perspectives?: WidgetsPerspective[];
}

export interface Props<Values extends WithPerspectives = WithPerspectives> {
  values?: Values;
  setValue: (prop: string, value?: unknown) => void;
}

const perspectiveTranslationTerms = {
  [WidgetsPerspective.MANAGER]: 'manager',
  [WidgetsPerspective.RESPONSIBLE_MANAGER]: 'responsibleManager',
  [WidgetsPerspective.RESPONSIBLE_HR]: 'responsibleHR',
  [WidgetsPerspective.EMPLOYEE]: 'employee',
};

export const ManagePerspectivesFactory = <Values extends WithPerspectives>(
  prohibitedPerspectives?: WidgetsPerspective[],
) => {
  const Component: React.FC<Props<Values>> = ({ values, setValue }) => {
    const { t } = useTranslation();

    const availablePerspectives = useAvailablePerspectives(
      prohibitedPerspectives,
    );

    if (!availablePerspectives.length) {
      return null;
    }

    return (
      <>
        <FormLabel component="legend">{t('perspectives')}</FormLabel>

        <FormGroup>
          {[
            WidgetsPerspective.EMPLOYEE,
            WidgetsPerspective.MANAGER,
            WidgetsPerspective.RESPONSIBLE_MANAGER,
            WidgetsPerspective.RESPONSIBLE_HR,
          ]
            .filter((perspective) => {
              return availablePerspectives.includes(perspective);
            })
            .map((perspective) => {
              return (
                <FormControlLabel
                  key={perspective}
                  control={
                    <Checkbox
                      checked={values?.perspectives?.includes(perspective)}
                      onChange={() => {
                        setValue(
                          'perspectives',
                          xor(values?.perspectives, [perspective]),
                        );
                      }}
                    />
                  }
                  label={`${t(perspectiveTranslationTerms[perspective])}`}
                />
              );
            })}
        </FormGroup>
      </>
    );
  };

  return Component;
};
